import React, { useRef, useState } from 'react';
import { Paperclip, X, FileText, Image as ImageIcon, FileSpreadsheet, FileArchive, FileType2 } from 'lucide-react';
import { toast } from 'sonner';

export const ATTACH_MAX_FILES = 5;
export const ATTACH_MAX_BYTES = 10 * 1024 * 1024;
const ATTACH_ALLOWED_PREFIXES = ['image/'];
const ATTACH_ALLOWED_EXACT = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/zip',
  'application/x-zip-compressed',
  'text/plain',
  'text/csv',
]);

const formatBytes = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const iconForMime = (ct, name) => {
  const t = (ct || '').toLowerCase();
  const ext = (name || '').split('.').pop().toLowerCase();
  if (t.startsWith('image/')) return <ImageIcon className="w-4 h-4 text-emerald-600" />;
  if (t === 'application/pdf' || ext === 'pdf') return <FileType2 className="w-4 h-4 text-rose-600" />;
  if (t.includes('spreadsheet') || ['xls', 'xlsx', 'csv'].includes(ext)) return <FileSpreadsheet className="w-4 h-4 text-emerald-700" />;
  if (t.includes('zip') || ext === 'zip') return <FileArchive className="w-4 h-4 text-amber-700" />;
  return <FileText className="w-4 h-4 text-ink-600" />;
};

const isAllowed = (ct) => {
  const t = (ct || '').toLowerCase();
  if (ATTACH_ALLOWED_PREFIXES.some((p) => t.startsWith(p))) return true;
  return ATTACH_ALLOWED_EXACT.has(t);
};

/**
 * Drag&drop + selector de adjuntos para composer de correo.
 *
 * Props:
 *   - value: File[]
 *   - onChange: (File[]) => void
 *   - testid?: string (default: 'composer-attachments')
 */
export default function AttachmentsInput({ value = [], onChange, testid = 'composer-attachments' }) {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const addFiles = (files) => {
    const incoming = Array.from(files);
    if (incoming.length === 0) return;
    const next = [...value];
    let added = 0;
    for (const f of incoming) {
      if (next.length >= ATTACH_MAX_FILES) {
        toast.error(`Máximo ${ATTACH_MAX_FILES} adjuntos por correo`);
        break;
      }
      if (f.size > ATTACH_MAX_BYTES) {
        toast.error(`'${f.name}' supera 10 MB (${formatBytes(f.size)})`);
        continue;
      }
      if (!isAllowed(f.type)) {
        toast.error(`'${f.name}' tiene un tipo no permitido (${f.type || 'desconocido'})`);
        continue;
      }
      // Evitar duplicados por name+size
      if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
      next.push(f);
      added++;
    }
    if (added > 0) onChange(next);
  };

  const removeAt = (i) => {
    const next = value.slice();
    next.splice(i, 1);
    onChange(next);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer?.files) addFiles(e.dataTransfer.files);
  };

  return (
    <div data-testid={testid}>
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded p-3 text-center transition cursor-pointer text-xs ${isDragging ? 'border-brand-400 bg-brand-50' : 'border-ink-200 hover:border-ink-300 bg-ink-50/40'}`}
        onClick={() => fileInputRef.current?.click()}
        data-testid={`${testid}-dropzone`}
      >
        <Paperclip className="w-4 h-4 mx-auto mb-1 text-ink-500" />
        <span className="text-ink-600">
          {isDragging ? 'Suelta los archivos aquí' : (
            <>Arrastra archivos o <span className="text-brand-600 underline">haz click para elegir</span></>
          )}
        </span>
        <p className="text-[10px] text-ink-400 mt-0.5">
          Máx {ATTACH_MAX_FILES} archivos · 10 MB c/u · PDF, Word, Excel, PPT, ZIP, imágenes, CSV, TXT
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.csv,.txt,image/*"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          data-testid={`${testid}-file-input`}
        />
      </div>
      {value.length > 0 && (
        <ul className="mt-2 space-y-1" data-testid={`${testid}-list`}>
          {value.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              className="flex items-center gap-2 text-xs bg-white border border-ink-200 rounded px-2 py-1"
              data-testid={`${testid}-item-${i}`}
            >
              {iconForMime(f.type, f.name)}
              <span className="flex-1 min-w-0 truncate text-ink-800">{f.name}</span>
              <span className="text-[10px] text-ink-500 shrink-0">{formatBytes(f.size)}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                className="text-ink-400 hover:text-rose-600"
                aria-label={`Quitar ${f.name}`}
                data-testid={`${testid}-remove-${i}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
